import { View, StyleSheet, Linking, Image } from "react-native";
import { useRef, useState } from "react";
import CustomText from "../../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../../components/common/general/CustomButton/CustomButton";
import { colors } from "../../../../styles/design-system";
import { CircleHelp, BookOpen, MessageSquare } from "lucide-react";
import onboardingVideo from "../../../../assets/onboarding_video.mp4";
import { useGetTenantSubmitHelpTicketLinkQuery } from "@axio/graphql/generated/graphql";

export const HelpContainer = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [submitButtonHovered, setSubmitButtonHovered] = useState(false);
  const isSafari = typeof navigator !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const safariVideoSrc = isSafari
    ? typeof onboardingVideo === "string"
      ? onboardingVideo
      : Image.resolveAssetSource(onboardingVideo)?.uri
    : undefined;

  const { data } = useGetTenantSubmitHelpTicketLinkQuery();
  const submitHelpTicketLink = data?.getTenantSubmitHelpTicketLink?.data;

  const handleSubmitTicket = () => {
    if (submitHelpTicketLink) {
      Linking.openURL(submitHelpTicketLink);
    }
  };

  return (
    <View style={styles.outerContainer}>
      {/* Need Help Section */}
      <View style={styles.sectionContainer}>
        <View style={styles.needHelpContent}>
          <View style={styles.needHelpTextContent}>
            <View style={styles.sectionHeader}>
              <CircleHelp size={18} color="#6B7280" />
              <CustomText
                style={styles.sectionHeaderTitle}
                text="Need Help?"
                accessibilityLabel="Need Help?"
                accessibilityRole="text"
                aria-level="2"
                role="heading"
              />
            </View>
            <CustomText
              text="Having trouble or found a bug? Submit a help ticket and we'll get back to you."
              style={styles.sectionSubtitle}
            />
          </View>
          <CustomButton
            text="Submit Ticket"
            styleType="primaryLightened"
            style={[styles.submitButton, submitButtonHovered && styles.submitButtonHovered]}
            textStyle={styles.submitButtonText}
            onPress={handleSubmitTicket}
            onHover={setSubmitButtonHovered}
            size="s"
            leftIcon={<MessageSquare size={16} color="#fff" />}
            disabled={!submitHelpTicketLink}
          />
        </View>
      </View>

      {/* Video Tutorials Section */}
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <BookOpen size={18} color="#6B7280" />
          <CustomText
            style={styles.sectionHeaderTitle}
            text="Video Tutorials"
            accessibilityLabel="Video Tutorials"
            accessibilityRole="text"
            aria-level="2"
            role="heading"
          />
        </View>
        <CustomText text="Learn how to get the most out of AI Companion" style={styles.sectionSubtitle} />

        {/* Getting Started Video */}
        <View style={styles.videoSection}>
          <CustomText text="Getting Started" style={styles.videoSectionTitle} />
          <View style={styles.videoWrapper}>
            {typeof window !== "undefined" ? (
              <video
                ref={videoRef}
                controls
                playsInline
                preload="metadata"
                src={safariVideoSrc}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
                aria-label="AI Companion getting started video">
                {!isSafari ? <source src={onboardingVideo} type="video/mp4" /> : null}
                Your browser does not support the video tag.
              </video>
            ) : null}
          </View>
          <CustomText text="A quick introduction to navigating and using AI Companion" style={styles.videoCaption} />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  sectionContainer: {
    backgroundColor: "#fff",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  sectionHeaderTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#111827",
  },
  sectionSubtitle: {
    fontSize: 14,
    color: "#4B5563",
    marginTop: 4,
  },
  needHelpContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  needHelpTextContent: {
    flex: 1,
    gap: 4,
  },
  submitButton: {
    padding: 0, // Override the uniform padding from size="s"
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: colors.brand[500],
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    // @ts-expect-error - web-only style
    transitionProperty: "all",
    transitionDuration: "120ms",
    transitionTimingFunction: "ease-out",
  },
  submitButtonHovered: {
    transform: "scale(1.015)",
    backgroundColor: colors.brand[600],
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  submitButtonText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "500",
  },
  videoSection: {
    marginTop: 24,
  },
  videoSectionTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#111827",
    marginBottom: 12,
  },
  videoWrapper: {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    backgroundColor: "#111827",
    borderRadius: 8,
    overflow: "hidden",
  },
  videoCaption: {
    fontSize: 14,
    color: "#4B5563",
    marginTop: 8,
  },
});
